import { ChannelMemberSeq } from '@@types/Api/Channel';
import { UploadFileParam } from '@@types/Api/File';
import { LogFetchParam, SendLogParam } from '@@types/Api/Log';
import {
  InviteMemberResponse,
  RemoveMemberResponse,
  UpdateReadSeqParam,
  UpdateReadSeqResponse,
} from '@@types/Api/Member';
import Channel from '@@types/Channel';
import { CHANNEL_ID, ContentWrap, FILE_ID, MEMBER_ID, THREAD_CHANNEL_ID } from '@@types/common';
import Log, { FileLog } from '@@types/Log';
import LogsResponse from '@@types/Log/Response';

import { MemberStatus } from '~/constants';
import { isSharedChannel } from '~/services/channelService';
import Actions from '~/stores/Actions';
import WebsocketActions, { WebsocketHandlerType } from '~/stores/actions/WebsocketActions';
import { channelsStore } from '~/stores/ChannelsStore';
import { interTenantMembersStore } from '~/stores/InterTenantMembersStore';
import { api } from '~/stores/providers/Api2';
import { interTenantApi } from '~/stores/providers/InterTenantApi';
import { stores } from '~/stores/Stores';
import { userStore } from '~/stores/UserStore';
import { isNonEmpty } from '~/utils/fnUtil/fnUtil';
import { when } from 'mobx';
import { ifElse } from 'ramda';

class ProxyActions {
  constructor(private actions: Actions) {}

  private get canUseInterTenant() {
    return userStore.canUseInterTenant;
  }

  checkDuplicateTitle = (title: string): Promise<any> => {
    if (!this.canUseInterTenant) {
      return api.channel.checkDuplicateTitle(title);
    }
    return Promise.all([api.channel.checkDuplicateTitle(title), interTenantApi.channel.checkDuplicateTitle(title)]);
  };

  connectWebsocket = (websocketActions: WebsocketActions, onSuccess: () => void): void => {
    if (websocketActions.hasSocket()) return;
    when(
      () =>
        isNonEmpty(userStore.accessToken) &&
        (websocketActions.type === WebsocketHandlerType.InterTenant ? userStore.enabledShareChannelFeature : true),
      () => {
        websocketActions.connect().then(onSuccess, () => {});
      }
    );
  };

  closeWebsockets = () => {
    this.actions.websocketActions.close();
    if (this.canUseInterTenant) {
      this.actions.interTenantWebsocketActions.close();
    }
  };

  updateMeStatus = (memberStatus: MemberStatus): void => {
    if (this.canUseInterTenant) {
      this.actions.interTenantWebsocketActions.updateStatus(memberStatus);
    }
    this.actions.websocketActions.updateStatus(memberStatus);
  };

  fetchAndSetChannels = (): Promise<any> => {
    if (!this.canUseInterTenant) {
      this.actions.observeActions.observeCanUseInterTenant();
    }
    return Promise.all([
      this.actions.channelActions.fetchAndCache(),
      this.canUseInterTenant ? this.actions.channelActions.fetchAndCacheInterTenantChannels() : Promise.resolve(),
    ]);
  };

  fetchChannelReadSeq = (channelId: CHANNEL_ID): Promise<ChannelMemberSeq[]> => {
    return ifElse(
      this.isSharedChannelId,
      interTenantApi.channel.fetchChannelReadSeq,
      api.channel.fetchChannelReadSeq
    )(channelId);
  };

  createTopicChannel = (shared: boolean, params): Promise<Channel> => {
    return (
      shared
        ? interTenantApi.channel.createTopicChannel(params)
        : api.channel.createTopicChannel(userStore.orgId, params)
    ) as Promise<Channel>;
  };

  fetchLogs = (params: LogFetchParam): Promise<LogsResponse> => {
    return ifElse(
      this.isSharedChannelId,
      () => interTenantApi.log.fetchLogs(params),
      () => api.log.fetchLogs(params)
    )(params.channelId) as Promise<LogsResponse>;
  };

  sendLog = (params: SendLogParam): Promise<Log> => {
    return this.isSharedChannelId(params.channelId) ? interTenantApi.log.sendLog(params) : api.log.sendLog(params);
  };

  updateReadSeq = (params: UpdateReadSeqParam): Promise<UpdateReadSeqResponse> => {
    return this.isSharedChannelId(params.channelId)
      ? interTenantApi.me.updateReadSeq(params)
      : api.me.updateReadSeq(params);
  };

  fetchChannelMembers = (channel: Channel) => {
    isSharedChannel(channel)
      ? this.actions.channelMembersActions.fetchInterTenantChannelMembers(channel)
      : this.actions.channelMembersActions.fetchChannelsMembers([channel]);
  };

  removeChannelMembers = (channelId: CHANNEL_ID, memberIds: MEMBER_ID[]): Promise<RemoveMemberResponse> => {
    if (this.isSharedChannelId(channelId)) {
      const members = memberIds.map((id) => {
        const { tenantId } = interTenantMembersStore.getById(id);
        return { tenantId, memberId: id };
      });
      return interTenantApi.channel.removeMembers(channelId, members);
    }
    return api.channel.removeMembers(channelId, memberIds);
  };

  leaveChannel = (channelId: CHANNEL_ID, myId: MEMBER_ID): Promise<void> => {
    return this.isSharedChannelId(channelId)
      ? interTenantApi.channel.leaveChannel(channelId, myId)
      : api.channel.leaveChannel(channelId);
  };

  inviteChannelMembers = (
    channelId: CHANNEL_ID,
    memberIds: MEMBER_ID[],
    threadChannelId?: THREAD_CHANNEL_ID
  ): Promise<InviteMemberResponse> => {
    return this.isSharedChannelId(channelId)
      ? interTenantApi.channel.inviteChannelMembers(channelId, stores.user.tenantId, memberIds)
      : api.channel.inviteChannelMembers(channelId, memberIds, threadChannelId);
  };

  getMemberStatus = (channelId: CHANNEL_ID, memberId: MEMBER_ID) => {
    return this.isSharedChannelId(channelId)
      ? this.actions.interTenantWebsocketActions.getMemberStatus(memberId)
      : this.actions.websocketActions.getMemberStatus(memberId);
  };

  uploadFile = (params: UploadFileParam): Promise<FileLog> => {
    return this.isSharedChannelId(params.fileDummyLog.channelId)
      ? interTenantApi.file.uploadFile(params)
      : api.file.uploadFile(params);
  };

  checkFileAcl = (channelId: CHANNEL_ID, fileId: FILE_ID): Promise<string> => {
    return this.isSharedChannelId(channelId)
      ? interTenantApi.file.checkFileAcl(channelId, fileId)
      : api.file.checkFileAcl(channelId, fileId);
  };

  checkVideoFileAcl = (channelId: CHANNEL_ID, fileId: FILE_ID): Promise<string | undefined> => {
    return api.file.checkWebFileAcl(channelId, fileId);
  };

  uploadChannelImage = (channelId: CHANNEL_ID, image: File): Promise<Channel> => {
    return this.isSharedChannelId(channelId)
      ? interTenantApi.file.uploadChannelImage(channelsStore.getManagementChannelId(channelId), image)
      : api.file.uploadChannelImage(channelId, image);
  };

  updateChannel =
    (channelId: CHANNEL_ID) =>
    (params: Partial<Channel>): Promise<Channel> => {
      return this.isSharedChannelId(channelId)
        ? interTenantApi.channel.updateChannel(channelsStore.getManagementChannelId(channelId), params)
        : api.channel.updateChannel(channelId, params);
    };

  private isSharedChannelId = (channelId: CHANNEL_ID): boolean => {
    return channelsStore.isSharedChannelId(channelId);
  };
}

export default ProxyActions;
